<script setup>
  import { computed, onMounted, ref, watch } from 'vue'
  import CurriculumEvents from '@/components/Study/CurriculumEvents.vue'
  import { useEventsStore } from '@/stores/events'

  const props = defineProps({
    item: Object
  })

  const eventStore = useEventsStore()
  const events = ref([])
  const seminarTypes = ref([])
  const selectedSeminarType = ref('')
  const seminarUrl = ref('')
  const isLoadingEvents = ref(false)
  const hasEventsError = ref(false)
  const isLoadingSeminarTypes = ref(false)
  const hasSeminarTypesError = ref(false)
  let activeEventRequestId = 0

  const normalizeBoolean = (value) => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value !== 'string') return false

    return ['1', 'true'].includes(value.trim().toLowerCase())
  }

  const seminarSelectionActive = computed(() => normalizeBoolean(props.item?.seminarSelectionActive))
  const showEventMeta = computed(() => seminarSelectionActive.value ? !!selectedSeminarType.value : !!props.item?.seminarType)

  const resetEvents = () => {
    events.value = []
    seminarUrl.value = ''
  }

  const fetchSeminarEvents = async (seminarType) => {
    if (!seminarType) {
      activeEventRequestId += 1
      isLoadingEvents.value = false
      hasEventsError.value = false
      resetEvents()
      return
    }

    const requestId = ++activeEventRequestId

    isLoadingEvents.value = true
    hasEventsError.value = false
    resetEvents()

    try {
      const response = await eventStore.fetchSeminarEvents(seminarType)

      if (response?.error) {
        if (requestId !== activeEventRequestId) return

        hasEventsError.value = true
        return
      }

      if (requestId !== activeEventRequestId) return

      events.value = response?.items || []
      seminarUrl.value = response?.seminarUrl || ''
    } catch {
      if (requestId !== activeEventRequestId) return

      hasEventsError.value = true
    } finally {
      if (requestId === activeEventRequestId) {
        isLoadingEvents.value = false
      }
    }
  }

  const fetchSeminarTypes = async () => {
    isLoadingSeminarTypes.value = true
    hasSeminarTypesError.value = false
    seminarTypes.value = []

    try {
      const response = await eventStore.fetchSeminarTypes()

      if (response?.error) {
        hasSeminarTypesError.value = true
        return
      }

      seminarTypes.value = response?.items || []
    } catch {
      hasSeminarTypesError.value = true
    } finally {
      isLoadingSeminarTypes.value = false
    }
  }

  watch(selectedSeminarType, async (seminarType) => {
    if (!seminarSelectionActive.value) return

    await fetchSeminarEvents(seminarType)
  })

  onMounted(async () => {
    if (seminarSelectionActive.value) {
      await fetchSeminarTypes()
      return
    }

    if (!props.item?.seminarType) return

    await fetchSeminarEvents(props.item.seminarType)
  })
</script>

<template>
  <div
    class="step-list-content-item py-4 mt-4"
  >
    <div
      class="border-b pb-2 border-border grid grid-cols-12"
    >
      <p
        class="step-list-content-item-headline font-semibold mb-2 col-span-8"
      >
        {{ item.headline }}
      </p>
      <p
        v-if="showEventMeta"
        class="font-bold col-span-2"
      >
        Termine
      </p>
      <a
        v-if="showEventMeta && seminarUrl"
        class="col-span-2 flex flex-row justify-end"
        target="_blank"
        :href="seminarUrl"
      >
        Seminarinfos
      </a>
      <div
        v-else-if="showEventMeta"
        class="col-span-2"
      />
    </div>

    <div
      v-if="item.text"
      class="step-list-content-item-text mt-4"
      v-html="item.text"
    />

    <div
      v-if="seminarSelectionActive"
      class="mt-6 max-w-xl"
    >
      <select
        v-model="selectedSeminarType"
        aria-label="Offenes Seminar wählen"
        :disabled="isLoadingSeminarTypes || hasSeminarTypesError"
        class="seminar-selection-select"
      >
        <option value="">
          {{ isLoadingSeminarTypes ? 'Seminare werden geladen …' : 'Offenes Seminar wählen' }}
        </option>
        <option
          v-for="seminarType in seminarTypes"
          :key="seminarType.id"
          :value="String(seminarType.id)"
        >
          {{ seminarType.title }}
        </option>
      </select>
    </div>
    <p
      v-if="hasSeminarTypesError"
      class="mt-4"
    >
      Seminare konnten nicht geladen werden.
    </p>
    <p
      v-else-if="seminarSelectionActive && !isLoadingSeminarTypes && !seminarTypes.length"
      class="mt-4"
    >
      Derzeit sind keine Seminare verfügbar.
    </p>
    <p
      v-if="isLoadingEvents"
      class="mt-4"
    >
      Termine werden geladen …
    </p>
    <p
      v-else-if="hasEventsError"
      class="mt-4"
    >
      Termine konnten nicht geladen werden.
    </p>
    <CurriculumEvents
      v-else-if="events.length"
      :events="events"
    />
    <p
      v-else-if="showEventMeta"
      class="mt-4"
    >
      Derzeit sind keine Termine verfügbar.
    </p>
  </div>
</template>

<style lang="scss" scoped>
  .seminar-selection-select {
    @apply w-full appearance-none rounded-full border border-border bg-white py-3 pl-4 pr-12 text-sm text-body outline-none transition-colors duration-300 ease-in-out;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z' fill='%236D5D57'/%3E%3C/svg%3E");
    background-position: right 1rem center;
    background-repeat: no-repeat;
  }

  .seminar-selection-select:focus {
    @apply border-primary;
  }

  .step-list-content-item-link :deep(svg) {
    @apply h-4 w-4 transition-transform duration-300 ease-in-out;
  }

  .step-list-content-item-link:hover :deep(svg) {
    @apply translate-x-2;
  }

</style>
